import React, { useState, useEffect } from "react";
import { collection, onSnapshot } from "firebase/firestore";
import { db } from "../firebase/config";
import { useNavigate, useLocation } from "react-router-dom";
import { useWeekContext } from "../context/WeekContext";
import { MoodDayModal } from "./MoodDayModal";
import type { SymptomDaySummary } from "../utils/symptomUtils";

// ─────────────────────────────────────────────────────────────
// Types & helpers for mood summaries
// ─────────────────────────────────────────────────────────────

type DailyLog = {
  dateId: string; // "YYYY-MM-DD"
  mood?: {
    key?: string;
    score: number;
  };
};

const dateIdToDate = (dateId: string): Date => {
  const [y, m, d] = dateId.split("-").map((n) => parseInt(n, 10));
  return new Date(y, m - 1, d);
};

const getDateRange = (endDate: Date, days: number): string[] => {
  const dates: string[] = [];
  for (let i = days - 1; i >= 0; i--) {
    const d = new Date(endDate);
    d.setDate(d.getDate() - i);
    dates.push(formatDateId(d));
  }
  return dates;
};

const formatDateId = (d: Date): string => {
  const year = d.getFullYear();
  const month = `${d.getMonth() + 1}`.padStart(2, "0");
  const day = `${d.getDate()}`.padStart(2, "0");
  return `${year}-${month}-${day}`;
};

// ─────────────────────────────────────────────────────────────
// MoodPathLine — draws a line through the center of each dot
// ─────────────────────────────────────────────────────────────
function MoodPathLine({ chartLogsFull }: { chartLogsFull: DailyLog[] }) {
  const [pathD, setPathD] = React.useState("");

  React.useEffect(() => {
    const raf = requestAnimationFrame(() => {
      const chart = document.querySelector(".chart") as HTMLElement;
      if (!chart) return;

      const chartRect = chart.getBoundingClientRect();
      const logsWithMood = chartLogsFull.filter((log) => log.mood?.score != null);
      if (logsWithMood.length < 2) {
        setPathD("");
        return;
      }

      const points: { x: number; y: number }[] = [];

      for (const log of logsWithMood) {
        const dotEl = chart.querySelector(`[data-date-id="${log.dateId}"]`) as HTMLElement | null;
        if (!dotEl) continue;

        const dotRect = dotEl.getBoundingClientRect();
        const x = ((dotRect.left + dotRect.width / 2) - chartRect.left) / chartRect.width * 100;
        const y = ((dotRect.top + dotRect.height / 2) - chartRect.top) / chartRect.height * 100;

        points.push({ x, y });
      }

      if (points.length < 2) {
        setPathD("");
        return;
      }

      setPathD(points.map((p, i) => `${i === 0 ? "M" : "L"} ${p.x} ${p.y}`).join(" "));
    });

    return () => cancelAnimationFrame(raf);
  }, [chartLogsFull]);

  if (!pathD) return null;

  return (
    <svg
      width="100%"
      height="100%"
      viewBox="0 0 100 100"
      preserveAspectRatio="none"
      style={{ position: "absolute", top: 0, left: 0, pointerEvents: "none" }}
    >
      <path
        d={pathD}
        fill="none"
        className="mood-path-line"
        stroke="#1E5E5B"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

// ─────────────────────────────────────────────────────────────
// GhostDots — pulsing hollow circles for unlogged days
// Rendered inside the hit-area layer so X positions match exactly
// ─────────────────────────────────────────────────────────────
function GhostDots({
  chartLogsFull,
  containerRef,
}: {
  chartLogsFull: DailyLog[];
  containerRef: React.RefObject<HTMLDivElement | null>;
}) {
  const [midY, setMidY] = React.useState<number | null>(null);

  React.useEffect(() => {
    const raf = requestAnimationFrame(() => {
      // Find the level-3 (neutral) row inside .chart and measure its vertical center
      const level3 = document.querySelector(".chart [data-level='3']") as HTMLElement | null;
      const container = containerRef.current;
      if (!level3 || !container) return;

      const level3Rect = level3.getBoundingClientRect();
      const containerRect = container.getBoundingClientRect();

      // Y relative to the hit-area container (top: 0, bottom: 0 inside chart-container)
      const y = (level3Rect.top + level3Rect.height / 2) - containerRect.top;
      setMidY(y);
    });
    return () => cancelAnimationFrame(raf);
  }, [chartLogsFull, containerRef]);

  if (midY === null) return null;

  const unloggedLogs = chartLogsFull.filter((log) => log.mood?.score == null);
  if (unloggedLogs.length === 0) return null;

  return (
    <>
      <style>{`
        @keyframes ghostPulse {
          0%, 100% { opacity: 0.55; transform: translate(-50%, -50%) scale(1); }
          50%       { opacity: 0.9;  transform: translate(-50%, -50%) scale(1.18); }
        }
      `}</style>

      {unloggedLogs.map((log) => {
        const index = chartLogsFull.findIndex((l) => l.dateId === log.dateId);
        const leftPercent =
          chartLogsFull.length > 1
            ? (index / (chartLogsFull.length - 1)) * 100
            : 50;

        return (
          <div
            key={`ghost-${log.dateId}`}
            style={{
              position: "absolute",
              left: `${leftPercent}%`,
              top: midY,
              width: 16,
              height: 16,
              borderRadius: "50%",
              border: "2px dashed #1E5E5B",
              backgroundColor: "transparent",
              transform: "translate(-50%, -50%)",
              pointerEvents: "none",
              animation: "ghostPulse 2s ease-in-out infinite",
              // stagger animation per index so they don't all pulse in sync
              animationDelay: `${(index % 5) * 0.3}s`,
            }}
          />
        );
      })}
    </>
  );
}

// ─────────────────────────────────────────────────────────────
// Mood visual mapping
// ─────────────────────────────────────────────────────────────

type MoodVisual = {
  label: string;
  Svg: React.FC<MoodSvgProps>;
};
type MoodSvgProps = React.SVGProps<SVGSVGElement>;

const Mood1Svg: React.FC<MoodSvgProps> = (props) => (
  <svg {...props} xmlns="http://www.w3.org/2000/svg" width="23" height="23" viewBox="0 0 23 23" fill="none">
    <path d="M11.4894 0C9.21699 0 6.99564 0.673839 5.10622 1.93631C3.2168 3.19877 1.74418 4.99317 0.874581 7.09257C0.00497818 9.19198 -0.22255 11.5021 0.22077 13.7308C0.66409 15.9595 1.75835 18.0068 3.36516 19.6136C4.97198 21.2204 7.01919 22.3146 9.2479 22.758C11.4766 23.2013 13.7867 22.9738 15.8862 22.1041C17.9856 21.2345 19.78 19.7619 21.0424 17.8725C22.3049 15.9831 22.9787 13.7617 22.9787 11.4894C22.9787 8.44219 21.7682 5.51983 19.6136 3.36516C17.4589 1.21048 14.5365 0 11.4894 0ZM7.18086 6.70213C7.80194 6.7155 8.40397 6.91914 8.90564 7.28555C9.40731 7.65196 9.78447 8.16349 9.98618 8.75106C10.0769 8.98727 10.0704 9.24977 9.96811 9.4812C9.86585 9.71264 9.67614 9.89418 9.44043 9.98617C9.3312 10.0314 9.21398 10.0542 9.09575 10.0532C8.90288 10.0526 8.71468 9.99382 8.5558 9.88448C8.39691 9.77514 8.27476 9.62036 8.20532 9.44042C8.13689 9.21623 8.0026 9.01781 7.81989 8.87096C7.63718 8.72411 7.41452 8.63563 7.18086 8.61702C6.94719 8.63563 6.72453 8.72411 6.54182 8.87096C6.35912 9.01781 6.22482 9.21623 6.15639 9.44042C6.11808 9.56645 6.05418 9.68321 5.96869 9.78341C5.88319 9.88361 5.77795 9.96509 5.65952 10.0228C5.5411 10.0804 5.41206 10.113 5.28046 10.1186C5.14886 10.1241 5.01753 10.1024 4.8947 10.0548C4.77186 10.0073 4.66016 9.93492 4.56658 9.84223C4.47299 9.74954 4.39954 9.63854 4.35081 9.51616C4.30208 9.39379 4.27913 9.26268 4.28338 9.13104C4.28764 8.99939 4.31901 8.87003 4.37554 8.75106C4.57724 8.16349 4.9544 7.65196 5.45607 7.28555C5.95774 6.91914 6.55977 6.7155 7.18086 6.70213ZM7.65958 14.3617C7.91351 14.3617 8.15704 14.4626 8.3366 14.6421C8.51615 14.8217 8.61703 15.0652 8.61703 15.3191C8.61703 15.4915 8.98086 15.7979 9.57447 15.7979C10.1681 15.7979 10.5319 15.4915 10.5319 15.3191C10.5319 15.0652 10.6328 14.8217 10.8123 14.6421C10.9919 14.4626 11.2354 14.3617 11.4894 14.3617C11.7433 14.3617 11.9868 14.4626 12.1664 14.6421C12.3459 14.8217 12.4468 15.0652 12.4468 15.3191C12.4468 15.4915 12.8106 15.7979 13.4043 15.7979C13.9979 15.7979 14.3617 15.4915 14.3617 15.3191C14.3617 15.0652 14.4626 14.8217 14.6421 14.6421C14.8217 14.4626 15.0652 14.3617 15.3192 14.3617C15.5731 14.3617 15.8166 14.4626 15.9962 14.6421C16.1757 14.8217 16.2766 15.0652 16.2766 15.3191C16.2453 15.665 16.1462 16.0013 15.9849 16.3088C15.8236 16.6163 15.6033 16.8889 15.3365 17.1112C15.0697 17.3335 14.7618 17.5011 14.4302 17.6043C14.0987 17.7075 13.7501 17.7444 13.4043 17.7128C12.7189 17.7246 12.0478 17.5165 11.4894 17.1191C10.9309 17.5165 10.2598 17.7246 9.57447 17.7128C9.22867 17.7444 8.88003 17.7075 8.54849 17.6043C8.21694 17.5011 7.90899 17.3335 7.64223 17.1112C7.37547 16.8889 7.15514 16.6163 6.99383 16.3088C6.83252 16.0013 6.7334 15.665 6.70213 15.3191C6.70213 15.0652 6.80301 14.8217 6.98256 14.6421C7.16212 14.4626 7.40565 14.3617 7.65958 14.3617ZM17.7128 10.0532C17.5199 10.0526 17.3317 9.99382 17.1728 9.88448C17.0139 9.77514 16.8918 9.62036 16.8223 9.44042C16.7539 9.21623 16.6196 9.01781 16.4369 8.87096C16.2542 8.72411 16.0315 8.63563 15.7979 8.61702C15.5642 8.63563 15.3415 8.72411 15.1588 8.87096C14.9761 9.01781 14.8418 9.21623 14.7734 9.44042C14.6697 9.65868 14.4873 9.82965 14.2628 9.91905C14.0383 10.0084 13.7883 10.0096 13.563 9.92241C13.3376 9.83518 13.1536 9.66597 13.0478 9.44872C12.942 9.23147 12.9223 8.98226 12.9926 8.75106C13.1847 8.15826 13.5596 7.64155 14.0636 7.27507C14.5676 6.9086 15.1747 6.7112 15.7979 6.7112C16.421 6.7112 17.0282 6.9086 17.5322 7.27507C18.0361 7.64155 18.4111 8.15826 18.6032 8.75106C18.6939 8.98727 18.6874 9.24977 18.5851 9.4812C18.4829 9.71264 18.2932 9.89418 18.0575 9.98617C17.9482 10.0314 17.831 10.0542 17.7128 10.0532Z" fill="#1E5E5B"/>
  </svg>
);

const Mood2Svg: React.FC<MoodSvgProps> = (props) => (
  <svg {...props} xmlns="http://www.w3.org/2000/svg" width="23" height="23" viewBox="0 0 23 23" fill="none">
    <g clipPath="url(#clip0_322_1946)">
      <path d="M11.4894 0C9.21699 0 6.99564 0.673839 5.10622 1.93631C3.2168 3.19877 1.74418 4.99317 0.874581 7.09257C0.00497818 9.19198 -0.22255 11.5021 0.22077 13.7308C0.66409 15.9595 1.75835 18.0068 3.36516 19.6136C4.97198 21.2204 7.01919 22.3146 9.2479 22.758C11.4766 23.2013 13.7867 22.9738 15.8862 22.1041C17.9856 21.2345 19.78 19.7619 21.0424 17.8725C22.3049 15.9831 22.9787 13.7617 22.9787 11.4894C22.9787 8.44219 21.7682 5.51983 19.6136 3.36516C17.4589 1.21048 14.5365 0 11.4894 0ZM13.883 7.18085C13.883 6.92692 13.9839 6.68339 14.1634 6.50383C14.343 6.32428 14.5865 6.2234 14.8404 6.2234C15.0944 6.2234 15.3379 6.32428 15.5174 6.50383C15.697 6.68339 15.7979 6.92692 15.7979 7.18085V10.0532C15.7979 10.3071 15.697 10.5507 15.5174 10.7302C15.3379 10.9098 15.0944 11.0106 14.8404 11.0106C14.5865 11.0106 14.343 10.9098 14.1634 10.7302C13.9839 10.5507 13.883 10.3071 13.883 10.0532V7.18085ZM7.18086 7.18085C7.18086 6.92692 7.28173 6.68339 7.46129 6.50383C7.64084 6.32428 7.88437 6.2234 8.1383 6.2234C8.39223 6.2234 8.63576 6.32428 8.81532 6.50383C8.99488 6.68339 9.09575 6.92692 9.09575 7.18085V10.0532C9.09575 10.3071 8.99488 10.5507 8.81532 10.7302C8.63576 10.9098 8.39223 11.0106 8.1383 11.0106C7.88437 11.0106 7.64084 10.9098 7.46129 10.7302C7.28173 10.5507 7.18086 10.3071 7.18086 10.0532V7.18085ZM16.7553 14.8883C16.1887 15.7556 15.4149 16.4679 14.5038 16.961C13.5928 17.454 12.5732 17.7122 11.5372 17.7122C10.5013 17.7122 9.48171 17.454 8.57063 16.961C7.65956 16.4679 6.88574 15.7556 6.31915 14.8883C6.17949 14.675 6.13028 14.4149 6.18235 14.1654C6.23443 13.9158 6.38351 13.6971 6.59681 13.5574C6.81011 13.4178 7.07016 13.3686 7.31974 13.4206C7.56933 13.4727 7.788 13.6218 7.92766 13.8351C8.31752 14.4378 8.85209 14.9333 9.48258 15.2763C10.1131 15.6194 10.8194 15.7991 11.5372 15.7991C12.255 15.7991 12.9614 15.6194 13.5919 15.2763C14.2224 14.9333 14.757 14.4378 15.1468 13.8351C15.216 13.7295 15.3052 13.6385 15.4096 13.5674C15.5139 13.4963 15.6312 13.4464 15.7547 13.4206C15.8783 13.3949 16.0058 13.3937 16.1298 13.4171C16.2538 13.4406 16.372 13.4883 16.4777 13.5574C16.5833 13.6266 16.6742 13.7159 16.7454 13.8202C16.8165 13.9245 16.8663 14.0418 16.8921 14.1654C16.9179 14.2889 16.9191 14.4164 16.8956 14.5404C16.8722 14.6645 16.8245 14.7827 16.7553 14.8883Z" fill="#1E5E5B"/>
    </g>
    <defs>
      <clipPath id="clip0_322_1946">
        <rect width="22.9787" height="22.9787" fill="white"/>
      </clipPath>
    </defs>
  </svg>
);

const Mood3Svg: React.FC<MoodSvgProps> = (props) => (
  <svg {...props} xmlns="http://www.w3.org/2000/svg" width="23" height="23" viewBox="0 0 23 23" fill="none">
    <path d="M11.4894 0C9.21699 0 6.99564 0.673839 5.10622 1.93631C3.2168 3.19877 1.74418 4.99317 0.874581 7.09257C0.00497818 9.19198 -0.22255 11.5021 0.22077 13.7308C0.66409 15.9595 1.75835 18.0068 3.36516 19.6136C4.97198 21.2204 7.01919 22.3146 9.2479 22.758C11.4766 23.2013 13.7867 22.9738 15.8862 22.1041C17.9856 21.2345 19.78 19.7619 21.0424 17.8725C22.3049 15.9831 22.9787 13.7617 22.9787 11.4894C22.9787 8.44219 21.7682 5.51983 19.6136 3.36516C17.4589 1.21048 14.5365 0 11.4894 0ZM17.6936 14.7255C17.6387 14.9729 17.4879 15.1884 17.2743 15.3248C17.0607 15.4612 16.8018 15.5074 16.5543 15.4532C16.1484 15.3655 15.7344 15.3206 15.3192 15.3191C14.4799 15.3182 13.6507 15.5012 12.8898 15.8552C12.1289 16.2092 11.4548 16.7256 10.9149 17.3681C10.8259 17.4749 10.7147 17.5611 10.5891 17.6206C10.4634 17.6802 10.3263 17.7116 10.1872 17.7128C10.0051 17.7131 9.82675 17.6614 9.67297 17.5639C9.51918 17.4664 9.3964 17.3271 9.31901 17.1622C9.24162 16.9974 9.21284 16.8139 9.23604 16.6333C9.25924 16.4527 9.33347 16.2825 9.45 16.1426C10.1689 15.2851 11.0669 14.5956 12.0809 14.1226C13.0949 13.6495 14.2002 13.4043 15.3192 13.4043C15.8701 13.4016 16.4195 13.4627 16.9564 13.5862C17.0797 13.6123 17.1967 13.6626 17.3006 13.734C17.4045 13.8055 17.4932 13.8968 17.5617 14.0027C17.6302 14.1085 17.6771 14.2269 17.6998 14.3509C17.7224 14.475 17.7203 14.6023 17.6936 14.7255ZM9.09575 8.61702C9.09575 8.99575 8.98344 9.36598 8.77303 9.68088C8.56262 9.99578 8.26355 10.2412 7.91365 10.3862C7.56375 10.5311 7.17873 10.569 6.80728 10.4951C6.43583 10.4212 6.09462 10.2389 5.82682 9.97106C5.55902 9.70325 5.37664 9.36205 5.30276 8.9906C5.22887 8.61915 5.26679 8.23412 5.41172 7.88422C5.55666 7.53432 5.80209 7.23526 6.117 7.02485C6.4319 6.81443 6.80213 6.70213 7.18086 6.70213C7.68872 6.70213 8.17578 6.90387 8.53489 7.26299C8.894 7.6221 9.09575 8.10916 9.09575 8.61702ZM13.883 8.61702C13.883 8.23829 13.9953 7.86807 14.2057 7.55316C14.4161 7.23826 14.7152 6.99282 15.0651 6.84789C15.415 6.70296 15.8 6.66503 16.1715 6.73892C16.5429 6.81281 16.8841 6.99518 17.1519 7.26299C17.4197 7.53079 17.6021 7.87199 17.676 8.24344C17.7499 8.6149 17.7119 8.99992 17.567 9.34982C17.4221 9.69972 17.1766 9.99879 16.8617 10.2092C16.5468 10.4196 16.1766 10.5319 15.7979 10.5319C15.29 10.5319 14.803 10.3302 14.4438 9.97106C14.0847 9.61194 13.883 9.12488 13.883 8.61702Z" fill="#1E5E5B"/>
  </svg>
);

const Mood4Svg: React.FC<MoodSvgProps> = (props) => (
  <svg {...props} xmlns="http://www.w3.org/2000/svg" width="23" height="23" viewBox="0 0 23 23" fill="none">
    <g clipPath="url(#clip0_322_1934)">
      <path d="M11.4894 0C9.21699 0 6.99564 0.673839 5.10622 1.93631C3.2168 3.19877 1.74418 4.99317 0.874581 7.09257C0.00497818 9.19198 -0.22255 11.5021 0.22077 13.7308C0.66409 15.9595 1.75835 18.0068 3.36516 19.6136C4.97198 21.2204 7.01919 22.3146 9.2479 22.758C11.4766 23.2013 13.7867 22.9738 15.8862 22.1041C17.9856 21.2345 19.78 19.7619 21.0424 17.8725C22.3049 15.9831 22.9787 13.7617 22.9787 11.4894C22.9787 8.44219 21.7682 5.51983 19.6136 3.36516C17.4589 1.21048 14.5365 0 11.4894 0ZM6.94149 6.70213C7.32022 6.70213 7.69045 6.81443 8.00535 7.02485C8.32025 7.23526 8.56569 7.53432 8.71063 7.88422C8.85556 8.23412 8.89348 8.61915 8.81959 8.9906C8.74571 9.36205 8.56333 9.70325 8.29553 9.97106C8.02773 10.2389 7.68652 10.4212 7.31507 10.4951C6.94362 10.569 6.5586 10.5311 6.2087 10.3862C5.8588 10.2412 5.55973 9.99578 5.34932 9.68088C5.13891 9.36598 5.0266 8.99575 5.0266 8.61702C5.0266 8.10916 5.22835 7.6221 5.58746 7.26299C5.94657 6.90387 6.43363 6.70213 6.94149 6.70213ZM6.85532 16.0755C7.35829 15.3051 8.04542 14.6722 8.85455 14.2341C9.66367 13.796 10.5693 13.5666 11.4894 13.5666C12.4095 13.5666 13.3151 13.796 14.1242 14.2341C14.9333 14.6722 15.6204 15.3051 16.1234 16.0755C16.1919 16.1811 16.239 16.2992 16.2619 16.423C16.2848 16.5468 16.2831 16.6739 16.2568 16.7971C16.2306 16.9202 16.1804 17.037 16.109 17.1407C16.0377 17.2444 15.9466 17.3331 15.841 17.4016C15.7353 17.4701 15.6173 17.5172 15.4935 17.5401C15.3697 17.563 15.2426 17.5612 15.1194 17.535C14.9963 17.5088 14.8795 17.4586 14.7758 17.3872C14.6721 17.3158 14.5834 17.2248 14.5149 17.1191C14.1871 16.6153 13.7387 16.2014 13.2103 15.9148C12.682 15.6282 12.0904 15.4781 11.4894 15.4781C10.8883 15.4781 10.2968 15.6282 9.76841 15.9148C9.24006 16.2014 8.79164 16.6153 8.46383 17.1191C8.39503 17.2252 8.30592 17.3165 8.20164 17.3879C8.09735 17.4593 7.97997 17.5094 7.85624 17.5352C7.73252 17.561 7.60491 17.5621 7.48078 17.5383C7.35664 17.5146 7.23844 17.4665 7.13298 17.3968C6.92154 17.2579 6.77365 17.041 6.72162 16.7934C6.66959 16.5459 6.71766 16.2878 6.85532 16.0755ZM16.0372 10.5319C15.6585 10.5319 15.2883 10.4196 14.9734 10.2092C14.6585 9.99879 14.413 9.69972 14.2681 9.34982C14.1232 8.99992 14.0853 8.6149 14.1591 8.24344C14.233 7.87199 14.4154 7.53079 14.6832 7.26299C14.951 6.99518 15.2922 6.81281 15.6637 6.73892C16.0351 6.66503 16.4201 6.70296 16.77 6.84789C17.1199 6.99282 17.419 7.23826 17.6294 7.55316C17.8398 7.86807 17.9521 8.23829 17.9521 8.61702C17.9521 9.12488 17.7504 9.61194 17.3913 9.97106C17.0322 10.3302 16.5451 10.5319 16.0372 10.5319Z" fill="#1E5E5B"/>
    </g>
    <defs>
      <clipPath id="clip0_322_1934">
        <rect width="22.9787" height="22.9787" fill="white"/>
      </clipPath>
    </defs>
  </svg>
);

const Mood5Svg: React.FC<MoodSvgProps> = (props) => (
  <svg {...props} xmlns="http://www.w3.org/2000/svg" width="23" height="23" viewBox="0 0 23 23" fill="none">
    <g clipPath="url(#clip0_322_1938)">
      <path d="M11.4894 0C9.21699 0 6.99564 0.673839 5.10622 1.93631C3.2168 3.19877 1.74418 4.99317 0.874581 7.09257C0.00497818 9.19198 -0.22255 11.5021 0.22077 13.7308C0.66409 15.9595 1.75835 18.0068 3.36516 19.6136C4.97198 21.2204 7.01919 22.3146 9.2479 22.758C11.4766 23.2013 13.7867 22.9738 15.8862 22.1041C17.9856 21.2345 19.78 19.7619 21.0424 17.8725C22.3049 15.9831 22.9787 13.7617 22.9787 11.4894C22.9787 8.44219 21.7682 5.51983 19.6136 3.36516C17.4589 1.21048 14.5365 0 11.4894 0ZM5.74469 5.42872L8.61703 3.51383C8.72077 3.43026 8.84082 3.36926 8.96949 3.33473C9.09816 3.30021 9.23261 3.29291 9.36426 3.31331C9.49591 3.33371 9.62185 3.38136 9.73403 3.45322C9.84622 3.52507 9.94217 3.61954 10.0158 3.73059C10.0893 3.84164 10.1389 3.96683 10.1614 4.09814C10.1838 4.22946 10.1786 4.36401 10.1461 4.4932C10.1136 4.62239 10.0545 4.74337 9.97252 4.8484C9.89058 4.95344 9.78761 5.04022 9.67022 5.10319L6.79788 7.01809C6.64369 7.12608 6.45952 7.183 6.27128 7.18085C6.06672 7.17927 5.86805 7.11222 5.70437 6.98952C5.5407 6.86682 5.42062 6.69492 5.36174 6.49901C5.30286 6.30311 5.30828 6.09349 5.37719 5.90089C5.4461 5.70828 5.57489 5.54281 5.74469 5.42872ZM5.74469 8.70319C5.85469 8.64216 5.97564 8.6034 6.10063 8.58913C6.22563 8.57487 6.3522 8.58537 6.47313 8.62005C6.59406 8.65473 6.70697 8.7129 6.8054 8.79123C6.90384 8.86957 6.98587 8.96654 7.04681 9.0766C7.13789 9.21525 7.26344 9.32786 7.41114 9.40339C7.55884 9.47892 7.72363 9.51478 7.88937 9.50745C8.0535 9.5131 8.21634 9.47644 8.36222 9.40099C8.5081 9.32553 8.63211 9.21382 8.72235 9.0766C8.84931 8.85314 9.05984 8.68927 9.30763 8.62103C9.55542 8.5528 9.82016 8.5858 10.0436 8.71277C10.2671 8.83973 10.431 9.05026 10.4992 9.29805C10.5674 9.54584 10.5344 9.81058 10.4075 10.034C10.1503 10.4717 9.78188 10.8333 9.33956 11.0822C8.89724 11.3311 8.39686 11.4584 7.88937 11.4511C7.38349 11.4567 6.88509 11.3286 6.4446 11.0798C6.00411 10.831 5.63715 10.4702 5.38086 10.034C5.31488 9.9234 5.27192 9.80058 5.25453 9.67295C5.23714 9.54531 5.24569 9.41548 5.27966 9.29122C5.31363 9.16696 5.37232 9.05084 5.45222 8.9498C5.53213 8.84876 5.6316 8.76488 5.74469 8.70319ZM6.70213 17.0904C7.1826 16.2616 7.87245 15.5735 8.70255 15.0953C9.53266 14.617 10.4739 14.3652 11.4319 14.3652C12.39 14.3652 13.3312 14.617 14.1613 15.0953C14.9914 15.5735 15.6812 16.2616 16.1617 17.0904C16.1996 17.1541 16.2197 17.2269 16.2197 17.3011C16.2197 17.3752 16.1996 17.448 16.1617 17.5117C16.1256 17.5763 16.0727 17.6299 16.0087 17.667C15.9447 17.704 15.8719 17.7232 15.7979 17.7223C12.9226 17.0808 9.94125 17.0808 7.06596 17.7223C6.99199 17.7232 6.91916 17.704 6.85513 17.667C6.79111 17.6299 6.73826 17.5763 6.70213 17.5117C6.67247 17.4429 6.66214 17.3673 6.67227 17.2931C6.68239 17.2188 6.71258 17.1488 6.75958 17.0904H6.70213ZM18.0096 10.0053C17.7533 10.4415 17.3863 10.8023 16.9458 11.0511C16.5053 11.2999 16.0069 11.428 15.5011 11.4223C14.9936 11.4297 14.4932 11.3024 14.0509 11.0535C13.6086 10.8046 13.2401 10.4429 12.983 10.0053C12.856 9.78186 12.823 9.51712 12.8913 9.26933C12.9595 9.02154 13.1234 8.81101 13.3468 8.68404C13.5703 8.55708 13.835 8.52408 14.0828 8.59231C14.3306 8.66054 14.5411 8.82441 14.6681 9.04787C14.7583 9.1851 14.8823 9.29681 15.0282 9.37227C15.1741 9.44772 15.3369 9.48438 15.5011 9.47872C15.6668 9.48605 15.8316 9.4502 15.9793 9.37467C16.127 9.29914 16.2525 9.18653 16.3436 9.04787C16.4065 8.93848 16.4903 8.84255 16.5902 8.76555C16.6902 8.68854 16.8043 8.63198 16.9261 8.59908C17.0479 8.56619 17.175 8.5576 17.3001 8.57382C17.4252 8.59004 17.5459 8.63075 17.6553 8.69362C17.7647 8.75648 17.8606 8.84028 17.9377 8.94022C18.0147 9.04016 18.0712 9.1543 18.1041 9.2761C18.137 9.3979 18.1456 9.52499 18.1294 9.65011C18.1132 9.77523 18.0724 9.89593 18.0096 10.0053ZM13.0883 3.82979C13.2287 3.6199 13.4463 3.47397 13.6938 3.42376C13.9412 3.37355 14.1985 3.42313 14.4096 3.5617L17.2819 5.4766C17.4327 5.59655 17.5438 5.75922 17.6007 5.94334C17.6576 6.12746 17.6576 6.32446 17.6007 6.50858C17.5438 6.69269 17.4327 6.85536 17.2819 6.9753C17.131 7.09524 16.9475 7.16688 16.7553 7.18085C16.5835 7.17346 16.4173 7.11697 16.2766 7.01809L13.4043 5.10319C13.1994 4.97106 13.0526 4.76574 12.9939 4.52911C12.9352 4.29249 12.9689 4.04237 13.0883 3.82979Z" fill="#1E5E5B"/>
    </g>
    <defs>
      <clipPath id="clip0_322_1938">
        <rect width="22.9787" height="22.9787" fill="white"/>
      </clipPath>
    </defs>
  </svg>
);

const moodScoreToVisual = (score: number | null): MoodVisual | null => {
  if (score == null) return null;

  switch (Math.round(score)) {
    case 1: return { label: "very sad", Svg: Mood5Svg };
    case 2: return { label: "sad", Svg: Mood4Svg };
    case 3: return { label: "average", Svg: Mood3Svg };
    case 4: return { label: "delighted", Svg: Mood2Svg };
    case 5: return { label: "happy", Svg: Mood1Svg };
    default: return null;
  }
};

// ─────────────────────────────────────────────────────────────
// MoodSummaryCard component
// ─────────────────────────────────────────────────────────────

type MoodSummaryCardProps = { userId: string };

const MoodSummaryCard: React.FC<MoodSummaryCardProps> = ({ userId }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedTab, setSelectedTab] = useState<"7days" | "30days">("7days");
  const [dailyLogs, setDailyLogs] = useState<DailyLog[]>([]);

  // Ref for the hit-area container — GhostDots measures Y relative to this
  const hitAreaRef = React.useRef<HTMLDivElement>(null);

  const navigate = useNavigate();
  const location = useLocation();
  const { unlockedWeek } = useWeekContext();
  const [selectedMoodLog, setSelectedMoodLog] = useState<DailyLog | null>(null);

  useEffect(() => {
    if (!userId) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    const logsRef = collection(db, "users", userId, "recoveryDailyLogs");

    const unsubscribe = onSnapshot(
      logsRef,
      (snapshot) => {
        const logs: DailyLog[] = [];
        snapshot.forEach((snap) => {
          const data = snap.data() as { dateId?: string; mood?: { key?: string; score?: number } };
          if (!data.dateId || data.mood?.score == null) return;

          logs.push({ dateId: data.dateId, mood: { key: data.mood.key, score: data.mood.score } });
        });

        logs.sort((a, b) => (a.dateId < b.dateId ? -1 : a.dateId > b.dateId ? 1 : 0));
        setDailyLogs(logs);
        setLoading(false);
      },
      (err) => {
        console.error("Error loading mood summaries:", err);
        setError("We could not load your mood summary right now.");
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [userId]);

  const uniqueLogs = React.useMemo(() => {
    const map: Record<string, DailyLog> = {};
    dailyLogs.forEach((log) => { map[log.dateId] = log; });
    return Object.values(map).sort((a, b) => a.dateId.localeCompare(b.dateId));
  }, [dailyLogs]);

  const rangeDates = React.useMemo(() => {
    const dates = selectedTab === "7days" ? getDateRange(new Date(), 7)
      : selectedTab === "30days" ? getDateRange(new Date(), 30)
        : [];
    return dates;
  }, [selectedTab]);

  const chartLogsFull = React.useMemo(() => {
    const full = rangeDates.map((dateId) => {
      const log = uniqueLogs.find((l) => l.dateId === dateId);
      return log ?? { dateId, mood: undefined };
    });
    return full;
  }, [uniqueLogs, rangeDates]);

  const handleDayClick = (log: DailyLog) => {
    if (log.mood?.score != null) {
      setSelectedMoodLog(log);
    } else {
      navigate("/recovery/checkins/mood", {
        state: { dateId: log.dateId, week: unlockedWeek, from: "dashboard", backgroundLocation: location },
      });
    }
  };

  return (
    <>
      <section className="dashboard-card" style={{ position: "relative", minHeight: "250px" }}>
        <h3 className="dashboard-card-subtitle">A Glimpse into Your Mood</h3>

        <div className="tabs">
          <div
            className={`tab ${selectedTab === "7days" ? "tab-active" : "tab-inactive"}`}
            onClick={() => setSelectedTab("7days")}
          >
            Last 7 days
          </div>

          <div
            className={`tab ${selectedTab === "30days" ? "tab-active" : "tab-inactive"}`}
            onClick={() => setSelectedTab("30days")}
          >
            Last 30 days
          </div>
        </div>

        {/* --- HIGH QUALITY BRANDED SPINNER --- */}
        {loading && (
          <div style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            padding: "60px 0",
            width: "100%",
          }}>
            <div style={{
              width: "40px",
              height: "40px",
              border: "4px solid rgba(30, 94, 91, 0.1)",
              borderTop: "4px solid #1E5E5B",
              borderRadius: "50%",
              animation: "spin 0.8s linear infinite",
              marginBottom: "20px",
              boxSizing: "border-box",
            }} />
            <div style={{ fontFamily: "DM Sans, sans-serif", textAlign: "center" }}>
              <p style={{
                color: "#0c2624",
                fontSize: "14px",
                fontWeight: "600",
                letterSpacing: "2px",
                textTransform: "uppercase",
                margin: 0,
                animation: "pulse 1.5s ease-in-out infinite",
              }}>
                BodyOwn
              </p>
            </div>
            <style>{`
              @keyframes spin {
                0% { transform: rotate(0deg); }
                100% { transform: rotate(360deg); }
              }
              @keyframes pulse {
                0%, 100% { opacity: 1; }
                50% { opacity: 0.5; }
              }
            `}</style>
          </div>
        )}

        {!loading && error && <p className="dashboard-card-text">{error}</p>}

        {!loading && !error && uniqueLogs.length === 0 && (
          <div className="w-full bg-[#F5F5F5] rounded-[24px] p-8 flex flex-col items-center justify-center gap-3 my-6">
            <div className="w-[60px] h-[60px] bg-[#E5E7EB] rounded-full flex items-center justify-center mb-2">
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#9CA3AF" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10" />
                <path d="M8 14s1.5 2 4 2 4-2 4-2" />
                <line x1="9" y1="9" x2="9.01" y2="9" />
                <line x1="15" y1="9" x2="15.01" y2="9" />
              </svg>
            </div>
            <h4 className="font-dmSans font-semibold text-[18px] text-[#0C2624] text-center m-0">
              No mood logs yet
            </h4>
            <p className="font-dmSans text-[14px] text-[#6B7280] text-center max-w-[260px] leading-relaxed m-0 mb-2">
              When you check in with your mood, your daily and weekly trends will appear here.
            </p>
            <button
              onClick={() => navigate("/recovery/checkins/mood", { state: { week: unlockedWeek, from: "dashboard", backgroundLocation: location } })}
              className="bg-[#D1E0B7] text-[#0C2624] px-6 py-3 rounded-[16px] font-dmSans font-semibold text-[15px] hover:opacity-80 transition-opacity flex items-center gap-2"
            >
              Log your mood
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M5 12h14M12 5l7 7-7 7" />
              </svg>
            </button>
          </div>
        )}

        {!loading && !error && uniqueLogs.length > 0 && (
          <div className={`chart-wrapper${selectedTab === "30days" ? " chart-wrapper--scrollable" : ""}`}>
            <div className="chart-container" style={{ position: "relative" }}>
              <div className="chart">
                {[5, 4, 3, 2, 1].map((level) => {
                  const mood = moodScoreToVisual(level);
                  const Svg = mood?.Svg;
                  return (
                    <div key={level} className={`scale scale-${level}`} data-level={level}>
                      <div className="mood-icon">{Svg && <Svg />}</div>
                      <div className="progress-line">
                        {chartLogsFull
                          .filter((log) => log.mood?.score === level)
                          .map((log) => {
                            const index = chartLogsFull.findIndex((l) => l.dateId === log.dateId);
                            const leftPercent =
                              chartLogsFull.length > 1
                                ? (index / (chartLogsFull.length - 1)) * 100
                                : 50;
                            return (
                              <div
                                key={log.dateId}
                                data-date-id={log.dateId}
                                className="mood-point"
                                style={{ left: `${leftPercent}%` }}
                              />
                            );
                          })}
                      </div>
                    </div>
                  );
                })}
                <MoodPathLine chartLogsFull={chartLogsFull} />
              </div>

              {/*
                Hit-area layer + ghost dots.
                Both use the SAME leftPercent formula so ghost dots sit
                exactly where the invisible hit targets are.
              */}
              <div
                ref={hitAreaRef}
                style={{ position: "absolute", top: 0, bottom: 0, left: 40, right: 0, zIndex: 10 }}
              >
                {/* Clickable hit areas */}
                {chartLogsFull.map((log, index) => {
                  const leftPercent = chartLogsFull.length > 1 ? (index / (chartLogsFull.length - 1)) * 100 : 50;
                  return (
                    <div
                      key={`hit-${log.dateId}`}
                      onClick={() => handleDayClick(log)}
                      title={log.mood?.score != null ? "View details" : "Log mood"}
                      style={{
                        position: "absolute",
                        left: `calc(${leftPercent}% - 15px)`,
                        width: "30px",
                        height: "100%",
                        cursor: "pointer",
                      }}
                    />
                  );
                })}

                {/* Ghost dots — rendered inside the same layer, same coordinate space */}
                <GhostDots chartLogsFull={chartLogsFull} containerRef={hitAreaRef} />
              </div>

              <div className="dates">
                {chartLogsFull.map((log, index) => {
                  const dateObj = dateIdToDate(log.dateId);
                  const dateLabel = dateObj.toLocaleDateString("en-GB", {
                    day: "numeric",
                    month: "short",
                  });
                  const leftPercent =
                    chartLogsFull.length > 1
                      ? (index / (chartLogsFull.length - 1)) * 100
                      : 50;
                  return (
                    <span
                      key={log.dateId}
                      className="date-label"
                      style={{
                        position: "absolute",
                        left: `${leftPercent}%`,
                        transform: "translateX(-50%)",
                      }}
                    >
                      {dateLabel}
                    </span>
                  );
                })}
              </div>
            </div>
          </div>
        )}
      </section>

      {selectedMoodLog && (
        <MoodDayModal
          day={{
            dateId: selectedMoodLog.dateId,
            log: { mood: selectedMoodLog.mood } as SymptomDaySummary["log"],
            weekdayLabel: "",
            risk: "none",
            alertSymptoms: [],
          }}
          onClose={() => setSelectedMoodLog(null)}
        />
      )}
    </>
  );
};

export default MoodSummaryCard;
